import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, FileText, MapPin, TrendingUp, PenLine, ArrowRight } from "lucide-react";
import { fmtD } from "../lib/date";
import { roiRange as rr } from "../lib/roi";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const money = (n) => "$" + Math.round(Number(n || 0)).toLocaleString();

export default function EstimateView() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [signMsg, setSignMsg] = useState("");
  const [wide, setWide] = useState(() => window.matchMedia("(min-width: 768px)").matches);
  const [previewFailed, setPreviewFailed] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const h = (e) => setWide(e.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, []);

  useEffect(() => {
    setData(null); setPreviewFailed(false);
    axios.get(`${BACKEND}/api/public/estimate/${token}`)
      .then((r) => setData(r.data))
      .catch(() => setErr("报价单不存在或已失效"));
  }, [token]);

  if (err) return <div className="min-h-screen flex items-center justify-center text-slate-500" data-testid="estimate-view-error">{err}</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand-500" /></div>;

  const roi = data.roi || {};
  const pdfUrl = `${BACKEND}/api/public/estimate/${token}/pdf`;
  const newer = data.newerVersion;
  const proceedSign = async () => {
    try {
      const { data: r } = await axios.post(`${BACKEND}/api/public/estimate/${token}/request-sign`);
      if (r.hasContract && r.contractToken) { window.location.href = `/contract-sign/${r.contractToken}`; }
      else { setSignMsg("感谢您的信任！我们的项目顾问将尽快与您联系并准备正式合同。"); }
    } catch { setSignMsg("提交失败，请稍后重试或直接联系顾问。"); }
  };
  return (
    <div className="min-h-screen bg-slate-100" data-testid="estimate-view-page">
      <div className="bg-slate-900 text-white px-5 py-4 flex items-center gap-2">
        <span className="font-bold text-red-500 text-lg">ALIGCUS</span>
        <div><p className="text-sm font-semibold">阿里建筑 · ADU 报价单</p><p className="text-[11px] text-slate-300">ALIGCUS · ADU Quotation</p></div>
      </div>
      <div className="max-w-[760px] mx-auto p-4 space-y-4">
        {newer && (
          <div data-testid="estimate-view-newer" data-version={newer.version} className="rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 flex items-center gap-3 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <p className="text-sm font-semibold text-indigo-900">此报价已有更新版本 v{newer.version}{newer.total ? `（${money(newer.total)}）` : ""}</p>
              <p className="text-[11px] text-indigo-700">本页为 v{data.version || 1} 历史版本，价格与配置可能已调整，请以最新版本为准。</p>
            </div>
            <button type="button" data-testid="estimate-view-newer-btn" onClick={() => navigate(`/estimate/${newer.token}`)}
              className="inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-lg px-4 py-2">
              查看最新版本 v{newer.version}<ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}
        {data.validUntil && (
          <div data-testid="estimate-view-validity" className={`rounded-xl px-4 py-2.5 text-sm font-medium border ${data.expired ? "bg-slate-100 border-slate-200 text-slate-500" : "bg-amber-50 border-amber-200 text-amber-800"}`}>
            {data.expired ? "⚠ 本报价已过有效期，请联系顾问更新报价。" : `⏳ 报价有效期至 ${fmtD(data.validUntil)}，建议尽快确认。`}
          </div>
        )}
        <div className="bg-white rounded-2xl p-5 shadow-sm">
          <p className="text-[11px] text-slate-400">项目地址 PROJECT ADDRESS</p>
          <p className="text-xl font-bold text-slate-800 flex items-center gap-1.5"><MapPin className="h-4 w-4 text-red-500" />{data.address || data.clientName}</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
            {[["套餐", data.packageName], ["面积", `${Math.round(data.sf || 0)} sf`], ["户型", data.layout || "-"], ["层高", data.ceiling || "-"]].map((m, i) => (
              <div key={i} className={`rounded-lg px-3 py-2 ${i % 2 === 0 ? "bg-brand-500" : "bg-brand-500"} text-white`}>
                <p className="text-[10px] font-semibold text-white">{m[0]}</p>
                <p className="text-sm font-bold truncate">{m[1]}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl p-5 shadow-sm" data-testid="estimate-view-price">
          <p className="text-red-600 font-bold mb-2">价格明细 Price Breakdown</p>
          {(data.lineItems || []).map((li, i) => (
            <div key={i} className="flex justify-between text-sm py-1.5 border-b border-slate-50">
              <span className="text-slate-600 pr-2">{li.name}</span>
              <span className="text-slate-800 font-medium whitespace-nowrap">{money(li.amount)}</span>
            </div>
          ))}
          <div className="flex justify-between items-baseline mt-3">
            <span className="text-red-600 font-bold">预估总价 Estimated Total</span>
            <span className="text-2xl font-bold text-red-600" data-testid="estimate-view-total">{money(data.total)}</span>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-5 shadow-sm">
          <p className="text-red-600 font-bold mb-3 flex items-center gap-1.5"><TrendingUp className="h-4 w-4" />投资回报 ROI</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
            {[["月租金", rr(roi, "monthlyRentLow", "monthlyRentHigh", "monthlyRent", money)], ["回本周期", rr(roi, "paybackLow", "paybackHigh", "paybackYears", (v) => v, "年")], ["毛收益率", rr(roi, "grossYieldLow", "grossYieldHigh", "grossYield", (v) => `${v}%`)], ["净增值", `${money(roi.netEquityGain)}`]].map((m, i) => (
              <div key={i} className="rounded-lg bg-slate-50 border border-slate-100 py-2.5">
                <p className="text-[10px] text-slate-400">{m[0]}</p>
                <p className="text-sm font-semibold text-slate-800">{m[1]}</p>
                {i === 3 && roi.valueAdd ? <p className="text-[9px] text-slate-400">建成市值 {money(roi.valueAdd)}</p> : null}
              </div>
            ))}
          </div>
        </div>

        {/* 完整 PDF：桌面端内嵌直显（优先保存时已生成的文件）；手机端不支持内嵌 → 首页预览图 + 打开 / 下载按钮 */}
        {wide ? (
          <div className="rounded-xl border border-red-100 bg-white overflow-hidden" data-testid="estimate-view-pdf-embed">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100">
              <span className="text-sm font-semibold text-slate-700 inline-flex items-center gap-1.5"><FileText className="h-4 w-4 text-red-600" />完整报价单 PDF（含卫星图与投资分析）</span>
              <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="estimate-view-pdf" className="text-xs font-semibold text-red-700 hover:underline">新窗口打开 / 下载</a>
            </div>
            <object data={`${pdfUrl}#toolbar=1&navpanes=0&view=FitH`} type="application/pdf" className="w-full bg-slate-50 block" style={{ height: "85vh", minHeight: 640 }} data-testid="estimate-view-pdf-iframe" aria-label="ALIGCUS 报价单 PDF">
              {/* 浏览器无内嵌 PDF 能力时的回退：首页预览图 + 打开按钮 */}
              <div className="p-4 space-y-3" data-testid="estimate-view-pdf-fallback">
                {!previewFailed && <img src={`${BACKEND}/api/public/estimate/${token}/pdf-preview`} alt="报价单首页预览" className="mx-auto max-w-[620px] w-full rounded-lg border border-slate-200" onError={() => setPreviewFailed(true)} />}
                <a href={pdfUrl} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 bg-white border border-red-200 hover:bg-red-50 text-red-700 rounded-xl py-3 font-semibold transition-colors"><FileText className="h-5 w-5" />打开完整报价单 PDF</a>
              </div>
            </object>
          </div>
        ) : (
          <div className="space-y-2" data-testid="estimate-view-pdf-mobile">
            {!previewFailed && (
              <a href={pdfUrl} target="_blank" rel="noreferrer" className="block rounded-xl border border-slate-200 bg-white overflow-hidden">
                <img src={`${BACKEND}/api/public/estimate/${token}/pdf-preview`} alt="报价单首页预览" data-testid="estimate-view-pdf-preview" className="w-full" loading="lazy" onError={() => setPreviewFailed(true)} />
              </a>
            )}
            <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="estimate-view-pdf"
              className="flex items-center justify-center gap-2 bg-white border border-red-200 hover:bg-red-50 text-red-700 rounded-xl py-3 font-semibold transition-colors">
              <FileText className="h-5 w-5" />打开 / 下载完整报价单 PDF (含卫星图与投资分析)
            </a>
          </div>
        )}
        {signMsg ? (
          <div data-testid="estimate-sign-msg" className="rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-4 text-center text-sm font-medium">{signMsg}</div>
        ) : !data.expired && (
          <button onClick={proceedSign} data-testid="estimate-proceed-sign"
            className="w-full flex items-center justify-center gap-2 bg-brand-500 hover:bg-brand-600 text-white rounded-xl py-3.5 font-bold text-base transition-colors shadow-sm">
            <PenLine className="h-5 w-5" />立即签约 Proceed to Sign
          </button>
        )}
        <p className="text-center text-[11px] text-slate-400 pb-6">本报价为初步估算，最终价格以现场勘测与正式合同为准。<br />阿里建筑 ALIGCUS · 626-803-8000 / 408-900-6688</p>
      </div>
    </div>
  );
}
